import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Phone, Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#praxis', label: 'Praxis' },
    { href: '#ueber-mich', label: 'Über mich' },
    { href: '#leistungen', label: 'Leistungen' },
    { href: '#ablauf', label: 'Ablauf' },
    { href: '#kontakt', label: 'Kontakt' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-card/95 backdrop-blur-md shadow-card border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 lg:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-primary flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <span className="text-primary-foreground font-heading text-xl lg:text-2xl font-semibold">JN</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-heading font-heading text-lg lg:text-xl font-medium">JaNa Logo</span>
              <span className="block text-caption text-xs">Praxis für Logopädie</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-body font-body text-sm hover:text-primary transition-colors duration-300 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-primary after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:025553210812" className="flex items-center text-body hover:text-primary transition-colors duration-300">
              <Phone className="w-4 h-4 mr-2" />
              <span className="font-body text-sm">02555 3210812</span>
            </a>
            <Button
              variant="cta"
              size="default"
              onClick={(e) => scrollToSection(e, '#kontakt')}
            >
              Termin anfragen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü öffnen"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-card/98 backdrop-blur-md border-b border-border shadow-lg transition-all duration-300 ${
          isMobileMenuOpen
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="block text-body font-body text-base py-2 hover:text-primary transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
          <div className="pt-4 border-t border-border space-y-3">
            <a href="tel:025553210812" className="flex items-center text-body hover:text-primary transition-colors duration-300 py-2">
              <Phone className="w-4 h-4 mr-2" />
              <span className="font-body">02555 3210812</span>
            </a>
            <Button
              variant="cta"
              size="lg"
              className="w-full"
              onClick={(e) => scrollToSection(e, '#kontakt')}
            >
              Termin anfragen
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
